import { useReducer, useEffect, useRef } from 'react';
import API from '@common/crud';
import logException from '@util/js-error';

const initialState = { isLoading: false, hasError: false, data: [] };

const reducer = (state, action) => {
  switch (action.type) {
    case 'startLoad':
      return { ...state, isLoading: true, hasError: false };
    case 'endLoad':
      return { ...state, data: action.data, isLoading: false, hasError: false };
    case 'error':
      return { ...state, data: [], isLoading: false, hasError: true };
    default:
      return state;
  }
};

export const useHttpGetAndCache = ({ url, filter, skip }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const isMountedRef = useRef(true);
  const apiIdRef = useRef(0);
  const refetchRef = useRef();

  useEffect(() => {
    const api = API();
    const abortController = new AbortController();
    isMountedRef.current = true;

    const { onError: defaultOnError, rawGet } = api;

    /**
     * Need to change message key to statusText for proper Sentry reporting. When we don't care about
     * seeing SQL Connection Timeouts in Sentry anymore we can remove this
     */
    api.onError = error => {
      if (error.data.message === 'SQL Connection Timeout.') {
        return logException(error, { statusText: error.data.message });
      }

      return defaultOnError(error);
    };

    refetchRef.current = async bypassCache => {
      dispatch({ type: 'startLoad' });

      /**
       * The same API can be called multiple times, but we only want to change state if API call matches
       * value stored in ref; so we'll give each API call an "id" by incrementing apiIdRef.current
       */
      apiIdRef.current += 1;
      const currentAPIRefValue = apiIdRef.current;

      try {
        const dataFromApi = await rawGet(url, filter, bypassCache, {
          signal: abortController.signal
        });
        dispatch({ type: 'endLoad', data: dataFromApi });
      } catch (error) {
        /**
         * Only set error state if current value of apiIdRef matches the value of the API that's
         * currently loading and component is mounted
         */
        if (apiIdRef.current === currentAPIRefValue && isMountedRef.current) {
          dispatch({ type: 'error' });
        }
      }
    };

    if (url && Boolean(skip) === false) {
      refetchRef.current(false);
    }

    return () => {
      abortController.abort('Abort API Call');
      // Set isMountedRef to false so that we don't attempt to set state on unmounted components
      isMountedRef.current = false;
    };
  }, [filter, url, skip]);

  return { ...state, refetch: () => refetchRef.current(true) };
};
